import { initFederation } from '@angular-architects/native-federation';
const isDesktopEnv = (): boolean => typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;

const FEDERATION_RETRY_DELAYS_MS = [200, 500, 1000];
const PREFIX_LOG = '[Shell]';

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

const waitForRemoteEntry = async (url: string, retries = 10, delayMs = 200): Promise<void> => {
  console.log(`${PREFIX_LOG} Requesting [${url}] ...`);
  for (let i = 0; i < retries; i++) {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (res.ok) {
        console.log(`${PREFIX_LOG} Validating JSON from [${url}] ...`);
        await res.json();
        console.log(`${PREFIX_LOG} Pre-fetch validated [${url}] successfully!`);
        return;
      }
      console.log(`${PREFIX_LOG} Waiting ${delayMs}ms before retrying [${url}]...`);
    } catch (error) {
      console.warn(`${PREFIX_LOG} Pre-fetch attempt ${i + 1}/${retries} for [${url}] failed.`, error);
    }
    await sleep(delayMs);
  }
  throw new Error(`remoteEntry.json at [${url}] not ready after ${retries} attempts`);
};

const initFederationWithRetry = async (): Promise<void> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= FEDERATION_RETRY_DELAYS_MS.length; attempt++) {
    try {
      await initFederation();

      if (attempt > 0) {
        console.log(`${PREFIX_LOG} initFederation() succeeded on retry attempt ${attempt + 1}.`);
      }

      return;
    } catch (error) {
      lastError = error;

      if (attempt === FEDERATION_RETRY_DELAYS_MS.length) {
        break;
      }

      const delay = FEDERATION_RETRY_DELAYS_MS[attempt];
      console.warn(`${PREFIX_LOG} initFederation() attempt ${attempt + 1} failed. Retrying in ${delay}ms ...`, error);
      await sleep(delay);
    }
  }

  throw lastError;
};

const bootstrapApp = () => import('./bootstrap');
const isDesktop = isDesktopEnv();

console.log(`${PREFIX_LOG} Starting`, isDesktop ? 'DesktopApp' : 'WebApp');

const main = isDesktop ?
  waitForRemoteEntry('/remoteEntry.json')
      .then(() => initFederationWithRetry())
      .catch(err => {
        console.error(`${PREFIX_LOG} Unable to initialize federation. Bootstrapping application anyway.`, err);
      }) :
  initFederation();

main
  .then(() => bootstrapApp())
  .catch(err => {
    console.error(`${PREFIX_LOG} App startup failed. Contact support!`, err);
  });
